import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import api from "../api/client";

const LOGO_URL = "/images/logo.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [centresOpen, setCentresOpen] = useState(false);
  const [centres, setCentres] = useState([]);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCentresOpen(false);
  }, [location]);

  useEffect(() => {
    api.get("/centres").then((r) => setCentres(r.data)).catch(() => {});
  }, []);

  const isHome = location.pathname === "/";

  const navLinks = [
    { label: t.nav.home, href: isHome ? "#home" : "/" },
    { label: t.nav.services, href: isHome ? "#services" : "/#services" },
    { label: t.nav.team, href: "/equipe", isRoute: true },
    { label: t.nav.blog, href: "/actualites", isRoute: true },
    { label: t.nav.collaborate, href: "/collaborez", isRoute: true },
    { label: t.nav.contact, href: isHome ? "#contact" : "/#contact" },
  ];

  const langs = ["fr", "nl", "en", "it"];

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-md"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
          <img
            src={LOGO_URL}
            alt="Psymed"
            className="h-14 sm:h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
          {navLinks[0].isRoute ? (
            <Link to={navLinks[0].href} className="text-sm font-medium text-slate-600 hover:text-[#2E4DB5]">{navLinks[0].label}</Link>
          ) : (
            <a href={navLinks[0].href} className="text-sm font-medium text-slate-600 hover:text-[#2E4DB5]">{navLinks[0].label}</a>
          )}

          {/* Centres dropdown */}
          <div className="relative" onMouseEnter={() => setCentresOpen(true)} onMouseLeave={() => setCentresOpen(false)}>
            <Link
              to="/centres"
              className={`text-sm font-medium transition-colors flex items-center gap-1 ${location.pathname.startsWith("/centres") ? "text-[#2E4DB5] font-semibold" : "text-slate-600 hover:text-[#2E4DB5]"}`}
              data-testid="nav-centres"
            >
              {t.nav.centres} <ChevronDown className="w-3.5 h-3.5" />
            </Link>
            {centresOpen && centres.length > 0 && (
              <div className="absolute left-0 top-full pt-3 min-w-[240px]" data-testid="centres-dropdown">
                <div className="bg-white border border-slate-100 shadow-lg rounded-2xl py-2 overflow-hidden">
                  <Link to="/centres" className="block px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 font-semibold">
                    {t.centres.all_centres}
                  </Link>
                  <div className="h-px bg-slate-100 my-1 mx-3" />
                  {centres.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/centres/${c.slug}`}
                      className="block px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#2E4DB5]"
                      data-testid={`nav-centre-${c.slug}`}
                    >
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-400">{c.city}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) =>
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 hover:text-[#2E4DB5] ${
                  location.pathname === link.href
                    ? "text-[#2E4DB5] font-semibold"
                    : "text-slate-600"
                }`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-slate-600 hover:text-[#2E4DB5] transition-colors duration-200"
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        {/* Language Switcher + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <div className="flex items-center gap-1 border border-slate-200 rounded-full px-3 py-1.5" data-testid="language-switcher">
            <Globe className="w-3.5 h-3.5 text-slate-400 mr-1" />
            {langs.map((lang, i) => (
              <React.Fragment key={lang}>
                <button
                  onClick={() => setLanguage(lang)}
                  data-testid={`lang-${lang}`}
                  className={`text-xs font-semibold uppercase tracking-wide transition-colors ${
                    language === lang
                      ? "text-[#2E4DB5]"
                      : "text-slate-400 hover:text-slate-700"
                  }`}
                >
                  {lang}
                </button>
                {i < langs.length - 1 && (
                  <span className="text-slate-300 text-xs">|</span>
                )}
              </React.Fragment>
            ))}
          </div>
          <a
            href={isHome ? "#contact" : "/#contact"}
            className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity"
            data-testid="navbar-cta"
          >
            {t.nav.contact}
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-white border-t border-slate-100 animate-slide-down"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 space-y-4">
            <Link to="/centres" className="block text-base font-semibold text-slate-700 hover:text-[#2E4DB5] py-1">
              {t.nav.centres}
            </Link>
            {centres.map((c) => (
              <Link key={c.slug} to={`/centres/${c.slug}`} className="block text-sm text-slate-500 hover:text-[#2E4DB5] py-1 pl-4">
                → {c.name}
              </Link>
            ))}
            <div className="h-px bg-slate-100" />
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block text-base font-medium text-slate-700 hover:text-[#2E4DB5] py-1"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-base font-medium text-slate-700 hover:text-[#2E4DB5] py-1"
                >
                  {link.label}
                </a>
              )
            )}
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100" data-testid="mobile-language-switcher">
              <Globe className="w-4 h-4 text-slate-400" />
              {langs.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`text-sm font-semibold uppercase ${
                    language === lang ? "text-[#2E4DB5]" : "text-slate-400"
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
